import { LinkOutlined, MoonOutlined, SunOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { getLocale, history, Link } from '@umijs/max';
import { App, Button, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import { AvatarDropdown, AvatarName, Footer, LocaleToggle, Question } from '@/components';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';
import { setGlobalMessage } from '@/utils/globalMessage';
import { applyUIStyle, normalizeUIStyle } from '@/utils/uiStyle';
import SystemAccessGate from '@/components/SystemAccessGate';
import LoginSessionMonitor from '@/components/LoginSessionMonitor';
import { enhanceNavigationMenu, getNavigationSelectedKeys } from '@/utils/navigationMenu';
import './styles/navigation.less';

// 🔧 处理chunk加载失败（部署更新后缓存不一致导致）
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    const msg = event.reason?.message || event.reason || '';
    if (String(msg).includes('Loading chunk') || String(msg).includes('Failed to fetch dynamically imported module')) {
      const chunkFailedKey = 'chunk_failed_reload';
      if (!sessionStorage.getItem(chunkFailedKey)) {
        sessionStorage.setItem(chunkFailedKey, '1');
        window.location.reload();
      }
    }
  });
}

// AppWrapper 组件：初始化全局 message 和 notification 实例
const AppWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { message, notification } = App.useApp();
  
  useEffect(() => {
    // 设置全局 message 实例（提供给 @/utils/globalMessage）
    setGlobalMessage(message);
    // 同时挂载到 window 对象，供 requestErrorConfig.ts 使用
    (window as any).__ANTD_MESSAGE__ = message;
    (window as any).__ANTD_NOTIFICATION__ = notification;
  }, [message, notification]);

  return <><LoginSessionMonitor />{children}</>;
};

const isDev = process.env.NODE_ENV === 'development';
const isDevOrTest = isDev || process.env.CI;
const loginPath = '/user/login';

const PUBLIC_SINGLE_PATHS = [loginPath, '/user/register', '/user/register-result', '/manual-callback', '/home', '/admin773441', ''];
const PUBLIC_PREFIX_PATHS = ['/share/', '/form-share/', '/homepage/', '/docs', '/7c/'];

const isPublicRoute = (path: string) => {
  // 移除路径末尾的斜杠，以匹配 Cloudflare 的 URL 规范化
  const normalizedPath = path.replace(/\/$/, '');
  if (PUBLIC_SINGLE_PATHS.includes(normalizedPath)) {
    return true;
  }
  return PUBLIC_PREFIX_PATHS.some((prefix) => normalizedPath.startsWith(prefix));
};

/**
 * 获取会员状态
 */
const fetchMembershipStatus = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      return null;
    }

    // 生产环境使用相对路径，让 Nginx 代理；开发环境使用完整 URL
    // 开发环境强制使用本地后端
    const baseURL = isDev ? 'http://localhost:9000' : (process.env.API_BASE_URL || '');
    const response = await fetch(`${baseURL}/api/v1/membership/status-and-uv`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    const data = await response.json();
    if (data.success && data.data) {
      return data.data;
    }
  } catch (error) {
    console.error('获取会员状态失败:', error);
  }
  return null;
};

/**
 * 更新网站Favicon
 */
const updateFavicon = (url: string) => {
  if (!url) return;

  // 移除旧的favicon链接
  const existingFavicon = document.querySelector("link[rel*='icon']");
  if (existingFavicon) {
    existingFavicon.remove();
  }

  // 添加新的favicon链接
  const link = document.createElement('link');
  link.rel = 'icon';
  link.type = 'image/x-icon';
  link.href = url;
  document.head.appendChild(link);
};

/**
 * 获取网站配置
 */
const fetchSiteConfig = async () => {
  try {
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    const isLoginLikeRoute =
      pathname === loginPath ||
      pathname.startsWith(`${loginPath}/`) ||
      pathname.startsWith('/user/register') ||
      pathname.startsWith('/user/register-result');

    const endpoint = isLoginLikeRoute ? '/api/v1/login-config' : '/api/v1/public/site-config';
    const response = await fetch(endpoint, {
      cache: 'no-store',
    });
    const data = await response.json();
    if (data.code === 200) {
      applyUIStyle(data.data.uiStyle);
      // 更新页面标题和meta标签
      if (data.data.seoTitle) {
        document.title = data.data.seoTitle;
      }
      if (data.data.seoDesc) {
        let metaDesc = document.querySelector('meta[name="description"]');
        if (!metaDesc) {
          metaDesc = document.createElement('meta');
          metaDesc.setAttribute('name', 'description');
          document.head.appendChild(metaDesc);
        }
        metaDesc.setAttribute('content', data.data.seoDesc);
      }
      if (data.data.seoKeywords) {
        let metaKeywords = document.querySelector('meta[name="keywords"]');
        if (!metaKeywords) {
          metaKeywords = document.createElement('meta');
          metaKeywords.setAttribute('name', 'keywords');
          document.head.appendChild(metaKeywords);
        }
        metaKeywords.setAttribute('content', data.data.seoKeywords);
      }
      // 更新Favicon
      if (data.data.siteFavicon) {
        updateFavicon(data.data.siteFavicon);
      }
      // 将站点配置暴露到全局，供页面级组件直接读取（如复制主域名）
      (window as any).INITIAL_STATE = {
        ...(window as any).INITIAL_STATE,
        siteConfig: data.data,
      };
      return data.data;
    }
  } catch (error) {
    console.error('加载网站配置失败:', error);
  }
  return null;
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  siteConfig?: import('./types/site-config').SiteConfig;
  membership?: any;
}> {
  // 独立展示后台不加载真实账户、会员或站点数据。
  if (history.location.pathname.replace(/\/$/, '') === '/admin773441') {
    return {};
  }
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return (msg?.data as API.CurrentUser) || undefined;
    } catch (_error) {
      // 不在这里跳转，让调用方决定
      return undefined;
    }
  };

  // 加载网站配置
  const siteConfig = await fetchSiteConfig();

  // 从 localStorage 读取主题设置，如果没有则使用数据库配置的默认主题
  const savedTheme = typeof window !== 'undefined'
    ? localStorage.getItem('app-theme')
    : null;
  // 优先级: localStorage > 数据库配置 > defaultSettings
  const theme = savedTheme === 'light' || savedTheme === 'realDark'
    ? savedTheme
    : (siteConfig?.defaultThemeMode || defaultSettings.navTheme);

  if (typeof document !== 'undefined') {
    applyUIStyle(siteConfig?.uiStyle);
    document.documentElement.setAttribute(
      'data-prefers-color',
      theme === 'realDark' ? 'dark' : 'light',
    );
  }

  // 根据主题选择对应的Logo
  let adminLogo = defaultSettings.logo;
  if (siteConfig) {
    if (theme === 'light' && siteConfig.adminLogoLight) {
      adminLogo = siteConfig.adminLogoLight;
    } else if (theme === 'realDark' && siteConfig.adminLogoDark) {
      adminLogo = siteConfig.adminLogoDark;
    } else if (siteConfig.siteLogo) {
      // 如果没有设置后台Logo，则使用登录页Logo作为备选
      adminLogo = siteConfig.siteLogo;
    }
  }

  // 合并配置
  const settings = {
    ...defaultSettings,
    navTheme: theme,
    // 所有用户统一使用流式布局，不再读取历史宽度偏好。
    contentWidth: 'Fluid' as const,
    title: siteConfig?.siteTitle || defaultSettings.title,
    logo: adminLogo,
  };

  // 如果不是公开页面，执行鉴权
  const { location } = history;
  const isPublicPath = isPublicRoute(location.pathname);

  // 检查是否有 token
  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('token');

  if (!isPublicPath) {
    const currentUser = await fetchUserInfo();

    // 如果获取用户信息失败，立即跳转到登录页
    if (!currentUser) {
      history.push(loginPath);
      return {
        fetchUserInfo,
        currentUser: undefined,
        settings: settings as Partial<LayoutSettings>,
        siteConfig,
      };
    }

    // 获取会员状态（用于判断是否为 VIP5）
    const membership = await fetchMembershipStatus();
    return {
      fetchUserInfo,
      currentUser,
      settings: settings as Partial<LayoutSettings>,
      siteConfig,
      membership,
    };
  }

  // 公开页面：如果有 token，也尝试获取用户信息（用于菜单跳转时不会被重定向到登录页）
  if (hasToken) {
    const currentUser = await fetchUserInfo();
    const membership = currentUser ? await fetchMembershipStatus() : null;
    return {
      fetchUserInfo,
      currentUser,
      settings: settings as Partial<LayoutSettings>,
      siteConfig,
      membership,
    };
  }

  return {
    fetchUserInfo,
    settings: settings as Partial<LayoutSettings>,
    siteConfig,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  // 主题切换功能
  const handleThemeToggle = () => {
    const currentTheme = initialState?.settings?.navTheme || 'realDark';
    const newTheme = currentTheme === 'light' ? 'realDark' : 'light';

    // 保存到 localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('app-theme', newTheme);
      document.documentElement.setAttribute(
        'data-prefers-color',
        newTheme === 'realDark' ? 'dark' : 'light',
      );
    }

    // 根据主题选择对应的Logo
    const siteConfig = initialState?.siteConfig;
    let newLogo = initialState?.settings?.logo;

    if (siteConfig) {
      if (newTheme === 'light' && siteConfig.adminLogoLight) {
        newLogo = siteConfig.adminLogoLight;
      } else if (newTheme === 'realDark' && siteConfig.adminLogoDark) {
        newLogo = siteConfig.adminLogoDark;
      } else if (siteConfig.siteLogo) {
        newLogo = siteConfig.siteLogo;
      }
    }

    setInitialState((preInitialState) => ({
      ...preInitialState,
      settings: {
        ...preInitialState?.settings,
        navTheme: newTheme,
        logo: newLogo,
      },
    }));
  };

  const currentTheme = initialState?.settings?.navTheme || 'realDark';
  
  return {
    logo: initialState?.settings?.logo || '/logo.svg',
    actionsRender: () => [
      <LocaleToggle key="locale" />,
      <Tooltip
        key="theme"
        title={currentTheme === 'light' ? '切换到深色主题' : '切换到浅色主题'}
      >
        <Button
          type="text"
          icon={currentTheme === 'light' ? <MoonOutlined /> : <SunOutlined />}
          onClick={handleThemeToggle}
          className="top-nav-utility-button"
          aria-label={currentTheme === 'light' ? '切换到深色主题' : '切换到浅色主题'}
        />
      </Tooltip>,
      <Question key="doc" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => (
        <AvatarDropdown menu>{avatarChildren}</AvatarDropdown>
      ),
    },
    waterMarkProps: initialState?.siteConfig?.watermarkEnabled !== false ? {
      content: initialState?.siteConfig?.watermark || 'CHUHAI7.COM',
    } : undefined,
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 检查是否有 token（用于处理从公开页面跳转到需登录页面的情况）
      const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('token');
      if (!initialState?.currentUser && !hasToken && !isPublicRoute(location.pathname)) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        opacity: 0.4,
      },
    ],
    links: isDevOrTest
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuItemRender: (menuItemProps, defaultDom) => {
      const menuItemDom =
        menuItemProps.path === '/landing-page/ai' ? (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              width: '100%',
            }}
          >
            {defaultDom}
            <img
              src="https://images.chuhai7.com/images/1/1788485937965447705.png"
              alt="VIP"
              style={{ width: 'auto', height: 20, flex: 'none', objectFit: 'contain' }}
            />
          </span>
        ) : (
          defaultDom
        );

      if (menuItemProps.path === '/bbs') {
        return (
          <a
            href="https://bbs.chuhai7.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            {menuItemDom}
          </a>
        );
      }

      if (menuItemProps.isUrl || menuItemProps.children) {
        return menuItemDom;
      }

      if (menuItemProps.path) {
        return (
          <Link
            to={menuItemProps.path.replace('/*', '')}
            target={menuItemProps.target}
            aria-current={history.location.pathname === menuItemProps.path ? 'page' : undefined}
            className={/\/(?:service-management|form-management|page-stats)$/.test(menuItemProps.path) ? 'top-nav-section-start' : undefined}
          >
            {menuItemDom}
          </Link>
        );
      }

      return menuItemDom;
    },
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <App>
          <AppWrapper><SystemAccessGate>{children}</SystemAccessGate></AppWrapper>
          {isDevOrTest && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings: { ...settings, contentWidth: 'Fluid' },
                }));
              }}
            />
          )}
        </App>
      );
    },
    ...initialState?.settings,
    // 运行时设置也保持全宽，避免旧状态或开发设置重新启用定宽。
    contentWidth: 'Fluid',
    menuProps: {
      ...(getNavigationSelectedKeys(history.location.pathname)
        ? { selectedKeys: getNavigationSelectedKeys(history.location.pathname) }
        : {}),
    },
    // 动态设置标题
    title:
      initialState?.siteConfig?.siteTitle ||
      initialState?.settings?.title ||
      'Ant Design Pro',
    menuDataRender: (menuData) => enhanceNavigationMenu(menuData, getLocale()),
    ...(normalizeUIStyle(initialState?.siteConfig?.uiStyle) === 'ios'
      ? {
          menuDataRender: (menuData) => {
            const adCallbackMenu = menuData.find(
              (item) => item.path === '/ad-callback',
            );
            const callbackRouteItems = (adCallbackMenu?.children || []).map(
              (item) => ({
                ...item,
                hideInMenu: true,
                parentKeys: ['/landing-page', '/landing-page/ad-callback'],
              }),
            );

            return enhanceNavigationMenu(menuData
              .filter((item) => item.path !== '/ad-callback')
              .map((item) => {
                if (item.path !== '/landing-page') {
                  return item;
                }

                return {
                  ...item,
                  children: [
                    ...(item.children || []).map((child) =>
                      child.path === '/landing-page/ad-callback'
                        ? {
                            ...child,
                            name: 'ad-callback-selection-ios',
                            locale: 'menu.landing-page.ad-callback-selection-ios',
                            children: undefined,
                            hideInMenu: false,
                          }
                        : child,
                    ),
                    ...callbackRouteItems,
                  ],
                };
              }), getLocale());
          },
          headerTitleRender: (logo: React.ReactNode, _title: React.ReactNode, props: { isMobile?: boolean }) => {
            const brandTitle = String(
              initialState?.siteConfig?.siteTitle ||
                initialState?.settings?.title ||
                'Ant Design Pro',
            );

            if (props.isMobile) {
              return <a className="top-nav-brand-mark is-mobile">{logo}</a>;
            }

            return (
              <a className="top-nav-brand-mark" aria-label={brandTitle}>
                {logo}
                <span className="top-nav-brand-title">
                  <span className="top-nav-brand-name">{brandTitle}</span>
                </span>
              </a>
            );
          },
        }
      : {}),
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  // 生产环境使用相对路径（空字符串），让 Nginx 处理代理
  // 开发环境使用完整 URL 或环境变量
  // 开发环境强制使用本地后端
  baseURL: isDev ? 'http://localhost:9000' : (process.env.API_BASE_URL || ''),
  ...errorConfig,
};
